import React from 'react';
import {
  User,
  Settings,
  CreditCard,
  HelpCircle,
  LogOut,
  Bell,
  Shield
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface UserDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  onNavigate: (page: string) => void;
}

export function UserDropdown({ isOpen, onClose, onLogout, onNavigate }: UserDropdownProps) {
  const { user } = useAuth();

  if (!isOpen) return null;

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.email) return 'U';
    const email = user.email;
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  // Format role for display
  const formatRole = (role: string) => {
    return role.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const handleItemClick = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose}></div>

      {/* Dropdown Menu */}
      <div className="absolute right-6 top-20 w-72 bg-white rounded-lg shadow-xl border border-border z-50">
        {/* User Info */}
        <div className="p-4 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white text-lg font-semibold">
              {getUserInitials()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{user?.email || 'User'}</p>
              {user?.department && (
                <p className="text-sm text-muted-foreground truncate">{user.department}</p>
              )}
            </div>
          </div>
          <div className="mt-3 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full inline-block">
            {user?.role ? formatRole(user.role) : 'Staff'}
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-2">
          <button
            onClick={() => handleItemClick(() => onNavigate('account'))}
            className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-secondary transition-colors text-left"
          >
            <User className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">Account Settings</p>
              <p className="text-xs text-muted-foreground">Manage your profile</p>
            </div>
          </button>

          <button
            onClick={() => handleItemClick(() => onNavigate('settings'))}
            className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-secondary transition-colors text-left"
          >
            <Settings className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">Preferences</p>
              <p className="text-xs text-muted-foreground">Notifications & display</p>
            </div>
          </button>

          <button
            onClick={() => handleItemClick(() => onNavigate('billing'))}
            className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-secondary transition-colors text-left"
          >
            <CreditCard className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">Billing</p>
              <p className="text-xs text-muted-foreground">Plan & payment method</p>
            </div>
          </button>

          <button
            onClick={() => handleItemClick(() => onNavigate('notifications'))}
            className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-secondary transition-colors text-left"
          >
            <Bell className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium">Notifications</p>
              <p className="text-xs text-muted-foreground">Alert preferences</p>
            </div>
          </button>
        </div>

        <div className="border-t py-2">
          <button
            onClick={() => handleItemClick(() => onNavigate('help'))}
            className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-secondary transition-colors text-left"
          >
            <HelpCircle className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">Help & Support</span>
          </button>

          <button
            onClick={() => handleItemClick(() => onNavigate('security'))}
            className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-secondary transition-colors text-left"
          >
            <Shield className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">Security</span>
          </button>
        </div>

        <div className="border-t py-2">
          <button
            onClick={() => handleItemClick(onLogout)}
            className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-50 transition-colors text-left text-red-600"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </div>
    </>
  );
}
